/**
 * <product-question-form>
 *
 * Posts a customer question to the store's app proxy (/apps/questions), which forwards to
 * the Mechanic `user/questions/submit` intake. The proxy hides the webhook URL and puts
 * Shopify's storefront edge in front, but the path is still public and unauthenticated, so
 * every spam check lives here: a honeypot field, a minimum fill time, and a per-browser
 * rate limit.
 *
 * Required data attributes:
 *   data-endpoint     intake path (the app proxy)
 *   data-product-id   numeric product ID
 */

const RATE_LIMIT_KEY = 'dirty:product-questions';
const SENT_HASHES_KEY = 'dirty:product-questions:sent';
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000;
const RATE_LIMIT_MAX_PER_PRODUCT = 1;
const RATE_LIMIT_MAX_GLOBAL = 3;
const SENT_HASHES_MAX = 100;
const MIN_FILL_MS = 3000;

class ProductQuestionForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.honeypot = this.querySelector('[data-honeypot]');
    this.status = this.querySelector('[data-form-status]');
    this.statusMessage = this.querySelector('[data-form-status-message]');
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.submitLabel = this.submitButton ? this.submitButton.textContent : '';
    this.connectedAt = Date.now();

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;

    const recent = this.recentSubmissions();
    const questionHash = ProductQuestionForm.hashText(this.normalizedQuestion());

    // Identical question already sent from this browser — this product or any other, however
    // long ago. Hashes persist beyond the rate window (capped list, no expiry).
    if (this.sentHashes().includes(questionHash)) {
      this.showMessage(
        "Looks like you've already sent us this question — we'll email you as soon as it's answered.",
        'error'
      );
      return;
    }

    const productCount = recent.filter((entry) => entry.p === this.dataset.productId).length;
    if (productCount >= RATE_LIMIT_MAX_PER_PRODUCT) {
      this.showBlocked(
        "You've already asked a question about this product in the last hour — give us a chance to answer it first."
      );
      return;
    }

    if (recent.length >= RATE_LIMIT_MAX_GLOBAL) {
      this.showBlocked(
        "You've reached the question limit for now — give us a chance to catch up, then try again later."
      );
      return;
    }

    // Bots get the same confirmation a customer does — no signal about what tripped. This runs
    // AFTER the duplicate/limit checks: the fill timer counts from page load, so a fast human
    // (refresh with a restored form, then immediate submit) can trip it — they must still get
    // the honest duplicate/limit message, never a decoy success masking it.
    // The decoy records like a real send: "success" promises the question was sent, so an
    // identical retry must hit the duplicate alert — not sail through because the decoy
    // left no trace.
    if (this.looksAutomated()) {
      this.recordSubmission(questionHash);
      this.showSuccess();
      return;
    }

    this.setSubmitting(true);

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.buildPayload())
      });

      if (!response.ok) throw new Error(`Webhook responded ${response.status}`);

      this.recordSubmission(questionHash);
      this.showSuccess();
    } catch (error) {
      console.error('[product-question-form]', error);
      this.setSubmitting(false);
      this.showMessage(
        'Something went wrong sending your question. Please try again, or contact us directly.',
        'error'
      );
    }
  }

  buildPayload() {
    const data = new FormData(this.form);

    const payload = {
      product_id: this.dataset.productId,
      fname: (data.get('fname') || '').trim(),
      lname: (data.get('lname') || '').trim(),
      email: (data.get('email') || '').trim(),
      question: (data.get('question') || '').trim()
    };

    // Omitted entirely when unchecked — the webhook must never receive a falsy value.
    if (data.get('marketing_consent')) payload.marketing_consent = 'on';

    return payload;
  }

  /** Honeypot filled, or the form was completed faster than a human plausibly could. */
  looksAutomated() {
    if (this.honeypot && this.honeypot.value.trim() !== '') return true;
    return Date.now() - this.connectedAt < MIN_FILL_MS;
  }

  /** The question, whitespace-collapsed and lowercased, so trivial edits don't dodge the dupe check. */
  normalizedQuestion() {
    return (this.form.elements.question.value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /** FNV-1a — tiny non-crypto hash, so stored entries carry no question text. */
  static hashText(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }

  /** Hashes of every question this browser has sent — newest last, capped, never expiring. */
  sentHashes() {
    try {
      return JSON.parse(window.localStorage.getItem(SENT_HASHES_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }

  /**
   * Recent sends from this browser as {t: timestamp, p: product ID}, entries outside the
   * rolling window dropped. Entries from older storage formats fail the `entry.t` check and
   * filter out harmlessly.
   */
  recentSubmissions() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(RATE_LIMIT_KEY) || '[]');
      const cutoff = Date.now() - RATE_LIMIT_WINDOW_MS;
      return stored.filter((entry) => entry && entry.t > cutoff);
    } catch (error) {
      return [];
    }
  }

  recordSubmission(questionHash) {
    try {
      const submissions = this.recentSubmissions()
        .concat({ t: Date.now(), p: this.dataset.productId });
      window.localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(submissions));

      const hashes = this.sentHashes().concat(questionHash).slice(-SENT_HASHES_MAX);
      window.localStorage.setItem(SENT_HASHES_KEY, JSON.stringify(hashes));
    } catch (error) {
      // Storage unavailable (private browsing) — the honeypot and fill timer still apply.
    }
  }

  setSubmitting(submitting) {
    this.submitting = submitting;
    if (!this.submitButton) return;

    this.submitButton.disabled = submitting;
    this.submitButton.setAttribute('aria-busy', submitting ? 'true' : 'false');
    this.submitButton.textContent = submitting ? 'Sending…' : this.submitLabel;
  }

  /**
   * Rate-limited: nothing the customer submits will go through for the rest of the window, so
   * the inputs come down with the alert. Duplicate and network alerts keep the form — editing
   * the question or retrying can still succeed there.
   */
  showBlocked(message) {
    this.form.hidden = true;
    this.showMessage(message, 'error');
  }

  showSuccess() {
    this.form.hidden = true;
    this.showMessage(
      this.dataset.labelSuccess || "Your question is with our team.",
      'success'
    );
  }

  showMessage(message, type) {
    if (!this.status) return;

    this.status.className = `alert my-3 ${
      type === 'success' ? 'bg-success-bg text-success-text' : 'bg-error-bg text-error-text'
    }`;
    (this.statusMessage || this.status).textContent = message;
    this.status.hidden = false;
    this.status.focus();
  }
}

if (!customElements.get('product-question-form')) {
  customElements.define('product-question-form', ProductQuestionForm);
}
